import { Autocomplete, type AutocompleteProps } from '@mantine/core';
import {
  type FieldValues,
  type UseControllerProps,
  useController,
} from 'react-hook-form';
import { useRules } from './hooks';

export type AutocompleteFieldProps<T extends FieldValues> =
  UseControllerProps<T> & Omit<AutocompleteProps, 'value' | 'defaultValue'>;

/**
 * AutocompleteField component
 *
 * @example
 * ```tsx
 * <AutocompleteField name="field.name" required data={['React', 'Angular', 'Svelte', 'Vue']}/>
 * ```
 *
 * @public
 * @param name - field name
 * @param control - form control
 * @param defaultValue - default value
 * @param rules - validation rules
 * @param shouldUnregister - unregister field when unmount
 * @param onChange - onChange event
 * @param required - is field required
 * @param props - other props
 */
export function AutocompleteField<T extends FieldValues>({
  name,
  control,
  defaultValue,
  rules,
  shouldUnregister,
  onChange,
  required,
  ...props
}: AutocompleteFieldProps<T>) {
  const innerRules = useRules({ required, ...rules });

  const {
    field: { value, onChange: fieldOnChange, ...field },
    fieldState,
  } = useController<T>({
    name,
    control,
    defaultValue,
    rules: innerRules,
    shouldUnregister,
  });

  return (
    <Autocomplete
      withinPortal
      error={fieldState.error?.message}
      value={value || ''}
      onChange={e => {
        fieldOnChange(e === '' ? null : e);
        onChange?.(e);
      }}
      {...field}
      {...props}
    />
  );
}
